/**
 * /app/screeners/setups — подбор сетапов (PULSE-372).
 *
 * «Готовые варианты»: ночной сервис setup-optimizer перебирает сетку условий
 * конструктора (окно × пороги) по 10 семействам на истории 1м-свечей с 24.03
 * и считает лонг/шорт на холдах 15м/1ч/4ч с комиссией — отдельно на «подборе»
 * (до split) и «проверке» (после). Сортировка по подбору; проверка —
 * подтверждение, а не критерий отбора (иначе подгонка).
 * «Свой вариант»: те же поля, что в скринере → заявка в очередь → тот же
 * расчёт по одной ячейке (worker, ~1–2 мин).
 * Из строки: «Бектест» (7 дней на графиках) и «Подключить» (скринер с
 * этими условиями и понятным названием).
 */
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Calculator, CheckCircle2, FlaskConical, Loader2, Plus } from 'lucide-react'

import {
  Field, INPUT, MetricBlocks, PRESETS, autoName, filtersSummary, metricsToParams, type MetricForms,
} from '@/components/screeners/metricForm'
import {
  type MetricParams,
  type SetupRequest,
  type SetupRow,
  type SetupRun,
  type UniversalParams,
  useCreateSetupRequest, useScreenerSetups, useScreenersOverview, useSetupRequests,
} from '@/hooks/useScreenersApi'
import { cn } from '@/lib/cn'

const FAMILIES: { key: string; label: string; hint: string }[] = [
  { key: 'div_pdn_oiup', label: 'Цена↓ OI↑', hint: 'дивергенция: цена падает, OI растёт' },
  { key: 'div_pup_oidn', label: 'Цена↑ OI↓', hint: 'дивергенция: цена растёт, OI падает' },
  { key: 'pump_oiup', label: 'Цена↑ OI↑', hint: 'рост цены вместе с ростом OI (набор позиций по тренду)' },
  { key: 'dump_oidn', label: 'Цена↓ OI↓', hint: 'падение цены вместе с падением OI (закрытие лонгов)' },
  { key: 'flat_oiup', label: 'Цена стоит, OI↑', hint: 'цена в коридоре, OI растёт' },
  { key: 'flat_oidn', label: 'Цена стоит, OI↓', hint: 'цена в коридоре, OI падает' },
  { key: 'gap_up', label: 'Гап вверх', hint: '1-мин свеча вверх ≥ порога' },
  { key: 'gap_down', label: 'Гап вниз', hint: '1-мин свеча вниз ≥ порога' },
  { key: 'pump', label: 'Памп', hint: 'рост цены за окно' },
  { key: 'dump', label: 'Дамп', hint: 'падение цены за окно' },
  { key: 'oi_up', label: 'OI↑', hint: 'рост открытого интереса за окно' },
  { key: 'oi_down', label: 'OI↓', hint: 'падение открытого интереса за окно' },
]

type SortKey = 'fit' | 'check' | 'mean' | 'freq'
const SORTS: Record<SortKey, { label: string; cmp: (a: SetupRow, b: SetupRow) => number }> = {
  fit: { label: 'по надёжности на подборе (честно)', cmp: (a, b) => (b.t_fit ?? -99) - (a.t_fit ?? -99) },
  check: { label: 'по надёжности на проверке', cmp: (a, b) => (b.t_check ?? -99) - (a.t_check ?? -99) },
  mean: {
    label: 'по худшему из двух результатов',
    cmp: (a, b) => Math.min(b.mean_fit ?? -99, b.mean_check ?? -99) - Math.min(a.mean_fit ?? -99, a.mean_check ?? -99),
  },
  freq: { label: 'по частоте сигналов', cmp: (a, b) => b.per_day - a.per_day },
}

const HOLD_LABEL: Record<number, string> = { 15: '15 мин', 60: '1 ч', 240: '4 ч' }
const SIDE_LABEL = { long: 'Лонг', short: 'Шорт' } as const

type Baseline = SetupRun['baseline']

const base = (b: Baseline, part: 'fit' | 'check', r: SetupRow): number =>
  b?.[part]?.[String(r.hold_min)]?.[r.side] ?? 0

/** Меньше 30 сделок в периоде — среднее держится на паре случаев. */
const MIN_TRADES = 30
const fewTrades = (r: SetupRow) => r.n_fit < MIN_TRADES || r.n_check < MIN_TRADES

/** Устойчив = в плюсе и лучше фона своей стороны на подборе И на проверке,
 *  на проверке t ≥ 2 (в падающем рынке любой шорт «в плюсе»), сделок хватает. */
function isRobust(r: SetupRow, b: Baseline): boolean {
  return !fewTrades(r) && (r.mean_fit ?? 0) > Math.max(0, base(b, 'fit', r)) &&
    (r.mean_check ?? 0) > Math.max(0, base(b, 'check', r)) && (r.t_check ?? 0) >= 2
}

const n = (v: unknown) => String(Number(v)).replace('.', ',')

/** Условия обычным языком: «Цена за 60 мин сдвинулась не больше ±2%, OI вырос на 20%+». */
function describe(p: UniversalParams): string {
  const get = (k: string) => {
    const m = p[k] as MetricParams | undefined
    return m && m.enabled !== false && m.direction ? m : undefined
  }
  const parts: string[] = []
  const pr = get('price')
  if (pr) {
    const w = pr.window_min
    parts.push(pr.direction === 'flat'
      ? `цена за ${w} мин сдвинулась не больше ±${n(pr.change_pct)}%`
      : `цена за ${w} мин ${pr.direction === 'pump' ? 'выросла' : 'упала'} на ${n(pr.change_pct)}%+`)
  }
  const oi = get('oi')
  if (oi) parts.push(`OI за ${oi.window_min} мин ${oi.direction === 'up' ? 'вырос' : 'упал'} на ${n(oi.change_pct)}%+`)
  const nd = get('nd')
  if (nd) {
    parts.push(`${nd.direction === 'up' ? 'покупок' : 'продаж'} больше на ${n(nd.change_pct)}%+ за ${nd.window_min} мин`)
  }
  const g = get('gap')
  if (g) {
    parts.push(`свеча за 1 мин ${g.direction === 'up' ? 'вверх' : 'вниз'} на ${n(g.change_pct)}%+` +
      ((g.window_min ?? 1) > 1 ? ` (в последние ${g.window_min} мин)` : ''))
  }
  const v = get('vol')
  if (v) parts.push(`объём за ${v.window_min} мин в ${n(v.change_pct)}+ раз выше обычного`)
  const l = get('liq')
  if (l) {
    const who = l.direction === 'long' ? 'лонгов' : l.direction === 'short' ? 'шортов' : ''
    parts.push(`ликвидаций ${who ? who + ' ' : ''}за ${l.window_min} мин от $${n(Math.round(Number(l.threshold_usd) / 1000))}k`)
  }
  const fu = get('fund')
  if (fu) parts.push(`фандинг ${fu.direction === 'pos' ? 'от +' : 'до −'}${n(fu.change_pct)}%`)
  const fs = filtersSummary(p)
  const s = parts.join(', ') + (fs ? ` (фильтры: ${fs})` : '')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function Pct({ v }: { v: number | null }) {
  if (v == null) return <span className="text-textFaint">—</span>
  return (
    <span className={v > 0 ? 'text-pulse' : 'text-rose'}>
      {v > 0 ? '+' : ''}{v.toFixed(2)}%
    </span>
  )
}

function T({ v }: { v: number | null }) {
  if (v == null) return <span className="text-textFaint">—</span>
  return <span className={cn(v >= 2 ? 'text-pulse' : v <= -2 ? 'text-rose' : 'text-textDim')}>{v.toFixed(1)}</span>
}

function Chip({ active, onClick, children, title }: {
  active: boolean; onClick: () => void; children: ReactNode; title?: string
}) {
  return (
    <button
      type="button" title={title} onClick={onClick}
      className={cn('px-2.5 py-1 text-xs border shrink-0 whitespace-nowrap',
        active ? 'border-fire/50 bg-fire/10 text-text' : 'border-line text-textDim hover:text-text')}
    >
      {children}
    </button>
  )
}

const fmtDate = (s: string) => new Date(s).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })

const HELP = (
  <>
    <p><b className="text-text">Что это.</b> Проверка на истории: что было бы, если входить по
      первому сигналу скринера (по одной монете не чаще раза в час) и держать позицию
      15 мин / 1 ч / 4 ч. Результат — после комиссии.</p>
    <p><b className="text-text">Как читать.</b> История делится на две части. «Подбор» — где искали
      лучшие варианты. «Проверка» — более поздний период, который в отбор не входил: если вариант
      и там в плюсе, это не случайная подгонка. <CheckCircle2 size={12} className="inline text-pulse" /> — держится на обоих.</p>
    <p><b className="text-text">Что дальше.</b> «Бектест» — посмотреть сигналы последних 7 дней на
      графиках. «Подключить» — создать скринер с этими условиями: останется указать
      Telegram-чат. Вкладка «Свой вариант» — проверить любые свои условия.</p>
  </>
)

/** Справка: на десктопе — три колонки, на телефоне — свёрнута (иначе занимает экран). */
function Help() {
  return (
    <>
      <div className="hidden sm:grid bg-ink border border-line px-4 py-3 mb-3 text-xs text-textDim leading-relaxed gap-1.5 sm:grid-cols-3">
        {HELP}
      </div>
      <details className="sm:hidden bg-ink border border-line mb-3 text-xs text-textDim leading-relaxed">
        <summary className="px-3 py-2.5 text-sm text-text cursor-pointer select-none">Как это работает?</summary>
        <div className="px-3 pb-3 space-y-2">{HELP}</div>
      </details>
    </>
  )
}

const holdText = (r: SetupRow) => HOLD_LABEL[r.hold_min] ?? `${r.hold_min} мин`

/** Условия, сторона, предупреждения — общее для строки таблицы и карточки. */
function RowHead({ r, baseline, compact }: { r: SetupRow; baseline: Baseline; compact: boolean }) {
  const robust = isRobust(r, baseline) && (
    <CheckCircle2 size={14} className="text-pulse shrink-0 mt-px" aria-label="держится на обоих периодах" />
  )
  return (
    <>
      {!compact && (
        <div className="text-text flex items-start gap-1.5">{robust}<span>{describe(r.params)}</span></div>
      )}
      <div className={cn('flex items-center gap-x-2 gap-y-1 flex-wrap', !compact && 'mt-1')}>
        {compact && robust}
        <span>
          <span className={r.side === 'long' ? 'text-pulse' : 'text-rose'}>→ {SIDE_LABEL[r.side].toLowerCase()}</span>
          <span className="text-textDim">, держать {holdText(r)}</span>
        </span>
        {fewTrades(r) && (
          <span className="text-2xs text-amber" title="меньше 30 сделок в одном из периодов — цифры могут быть случайностью">
            ⚠ мало сделок
          </span>
        )}
        {r.top_symbol_share > 0.3 && (
          <span className="text-2xs text-amber" title="результат сильно зависит от одной монеты">
            ⚠ {Math.round(r.top_symbol_share * 100)}% сделок — одна монета
          </span>
        )}
      </div>
    </>
  )
}

function RowActions({ r, universalId, big = false }: { r: SetupRow; universalId?: number; big?: boolean }) {
  if (universalId == null) return null
  const q = encodeURIComponent(JSON.stringify(r.params))
  const name = encodeURIComponent(`${autoName(r.params)} → ${SIDE_LABEL[r.side].toLowerCase()} ${holdText(r)}`)
  const btn = big ? 'flex-1 justify-center px-3 py-2 text-xs' : 'px-2 py-0.5 text-2xs'
  return (
    <span className={cn('flex gap-1.5', big ? 'w-full' : 'ml-auto')}>
      <Link
        to={`/app/screeners/${universalId}?params=${q}`}
        className={cn('inline-flex items-center gap-1 border border-line text-textDim hover:text-text', btn)}
        title="посмотреть сигналы последних 7 дней на графиках"
      >
        <FlaskConical size={12} /> Бектест
      </Link>
      <Link
        to={`/app/screeners?connect=${q}&name=${name}`}
        className={cn('inline-flex items-center gap-1 border border-pulse/30 text-pulse hover:bg-pulse/10', btn)}
        title="создать скринер с этими условиями — останется указать Telegram-чат"
      >
        <Plus size={12} /> Подключить
      </Link>
    </span>
  )
}

function PeriodBox({ title, n, mean, wr, t }: {
  title: string; n: number; mean: number | null; wr: number | null; t: number | null
}) {
  return (
    <div className="border border-line/60 px-2.5 py-2">
      <div className="text-2xs text-textFaint">{title}</div>
      <div className="text-base tabular-nums leading-tight mt-0.5"><Pct v={mean} /></div>
      <div className="text-2xs text-textDim tabular-nums mt-1 leading-snug">
        {n} сделок · {wr != null ? `${Math.round(wr * 100)}%` : '—'} в плюс
        <br />надёжность <T v={t} />
      </div>
    </div>
  )
}

/** Результаты: таблица на десктопе, карточки на телефоне. */
function SetupTable({ rows, baseline, universalId, compact = false }: {
  rows: SetupRow[]; baseline: Baseline; universalId?: number
  /** условия у всех строк одни (свой вариант) — показаны над таблицей */
  compact?: boolean
}) {
  const th = 'px-3 pb-2 font-medium cursor-help'
  return (
    <>
      <div className="md:hidden space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="bg-ink border border-line p-3 text-xs space-y-2.5">
            <div><RowHead r={r} baseline={baseline} compact={compact} /></div>
            <div className="grid grid-cols-2 gap-2">
              <PeriodBox title="Подбор" n={r.n_fit} mean={r.mean_fit} wr={r.wr_fit} t={r.t_fit} />
              <PeriodBox title="Проверка" n={r.n_check} mean={r.mean_check} wr={r.wr_check} t={r.t_check} />
            </div>
            <div className="text-textFaint">≈ {r.per_day.toFixed(1)} сигналов в день</div>
            <RowActions r={r} universalId={universalId} big />
          </div>
        ))}
      </div>
      <div className="hidden md:block bg-ink border border-line overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-textFaint border-b border-line text-xs">
              <th className="px-3 py-2 font-medium" rowSpan={2}>{compact ? 'Сделка' : 'Условия → сделка'}</th>
              <th className="px-3 py-2 font-medium text-right cursor-help whitespace-normal w-20" rowSpan={2}
                title="Сколько сигналов в среднем в день (по всем монетам)">Сигналов в день</th>
              <th className="px-3 pt-2 font-medium text-center border-l border-line/60 cursor-help" colSpan={4}
                title="Период, на котором искали лучшие варианты">Подбор</th>
              <th className="px-3 pt-2 font-medium text-center border-l border-line/60 cursor-help" colSpan={4}
                title="Более поздний период, не участвовал в отборе — честная проверка">Проверка</th>
            </tr>
            <tr className="text-right text-textFaint border-b border-line text-2xs">
              <GroupCols th={th} />
              <GroupCols th={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-line/40 hover:bg-line/20 align-top">
                <td className={cn('px-3 py-2 text-xs whitespace-normal', compact ? 'min-w-[230px]' : 'min-w-[340px]')}>
                  <RowHead r={r} baseline={baseline} compact={compact} />
                  <div className="mt-1 flex"><RowActions r={r} universalId={universalId} /></div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-textDim">{r.per_day.toFixed(1)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-textDim border-l border-line/40">{r.n_fit}</td>
                <td className="px-3 py-2 text-right tabular-nums"><Pct v={r.mean_fit} /></td>
                <td className="px-3 py-2 text-right tabular-nums text-textDim">
                  {r.wr_fit != null ? `${Math.round(r.wr_fit * 100)}%` : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums"><T v={r.t_fit} /></td>
                <td className="px-3 py-2 text-right tabular-nums text-textDim border-l border-line/40">{r.n_check}</td>
                <td className="px-3 py-2 text-right tabular-nums"><Pct v={r.mean_check} /></td>
                <td className="px-3 py-2 text-right tabular-nums text-textDim">
                  {r.wr_check != null ? `${Math.round(r.wr_check * 100)}%` : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums"><T v={r.t_check} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function GroupCols({ th }: { th: string }) {
  return (
    <>
      <th className={cn(th, 'border-l border-line/60')} title="Сколько было сделок">сделок</th>
      <th className={th} title="Средний результат одной сделки после комиссии">в среднем</th>
      <th className={th} title="Доля прибыльных сделок">прибыльных</th>
      <th className={th} title="Надёжность: 2 и выше — результат вряд ли случаен, ниже 1 — скорее шум">надёжность</th>
    </>
  )
}

function BaselineLine({ b }: { b: Baseline }) {
  const vals = Object.values(b ?? {}).flatMap((part) =>
    Object.values(part).flatMap((x) => [x.long, x.short]))
  if (!vals.length) return null
  const lo = Math.min(...vals), hi = Math.max(...vals)
  const f = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(2).replace('.', ',')}%`
  return (
    <p className="hidden sm:block text-xs text-textDim mb-3">
      Для сравнения: вход наугад в любую минуту давал от {f(lo)} до {f(hi)} за сделку
      (в основном это комиссия). <CheckCircle2 size={12} className="inline text-pulse" /> ставится,
      только если вариант лучше этого и в подборе, и в проверке.
    </p>
  )
}

function ReadyTab({ universalId }: { universalId?: number }) {
  const [family, setFamily] = useState('div_pdn_oiup')
  const [side, setSide] = useState<'all' | 'long' | 'short'>('all')
  const [hold, setHold] = useState(0)
  const [minPerDay, setMinPerDay] = useState(0)
  const [robustOnly, setRobustOnly] = useState(false)
  const [minMean, setMinMean] = useState<number | null>(null)
  const [minT, setMinT] = useState<number | null>(null)
  const [minTrades, setMinTrades] = useState(0)
  const [sort, setSort] = useState<SortKey>('fit')
  const { data, isLoading, isError } = useScreenerSetups(family)
  const run = data?.run
  const baseline = run?.baseline ?? null

  const filtered = useMemo(() => (data?.rows ?? []).filter((r) =>
    (side === 'all' || r.side === side) &&
    (hold === 0 || r.hold_min === hold) &&
    r.per_day >= minPerDay &&
    (minMean == null || ((r.mean_fit ?? -1e9) > minMean && (r.mean_check ?? -1e9) > minMean)) &&
    (minT == null || ((r.t_fit ?? -1e9) >= minT && (r.t_check ?? -1e9) >= minT)) &&
    Math.min(r.n_fit, r.n_check) >= minTrades &&
    (!robustOnly || isRobust(r, baseline))),
  [data, side, hold, minPerDay, minMean, minT, minTrades, robustOnly, baseline])
  const rows = useMemo(() => [...filtered].sort(SORTS[sort].cmp).slice(0, 100), [filtered, sort])
  const extraOn = minMean != null || minT != null || minTrades > 0 || sort !== 'fit'

  return (
    <>
      {run && (
        <p className="hidden sm:block text-xs text-textFaint mb-2">
          История {fmtDate(run.data_from)}–{fmtDate(run.data_to)}, подбор до {fmtDate(run.split_ts)}, проверка
          после · комиссия {run.fee_pct}% · пересчитывается каждую ночь
          (последний: {new Date(run.finished_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})
          · проверено {run.n_variants} вариантов, поэтому часть «хороших» на подборе — случайность: смотрите на проверку
        </p>
      )}
      <BaselineLine b={baseline} />
      <div className="bg-ink border border-line p-3 space-y-3 mb-3">
        <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap -mx-3 px-3 sm:mx-0 sm:px-0 pb-1 sm:pb-0">
          {FAMILIES.map((f) => (
            <Chip key={f.key} active={family === f.key} onClick={() => setFamily(f.key)} title={f.hint}>
              {f.label}
            </Chip>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 text-xs">
          <div className="flex gap-1">
            {(['all', 'long', 'short'] as const).map((s) => (
              <Chip key={s} active={side === s} onClick={() => setSide(s)}>
                {s === 'all' ? 'Лонг и шорт' : SIDE_LABEL[s]}
              </Chip>
            ))}
          </div>
          <div className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[0, 15, 60, 240].map((h) => (
              <Chip key={h} active={hold === h} onClick={() => setHold(h)}>
                {h === 0 ? 'Любое удержание' : HOLD_LABEL[h]}
              </Chip>
            ))}
          </div>
          <div className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[0, 0.5, 1, 3].map((v) => (
              <Chip key={v} active={minPerDay === v} onClick={() => setMinPerDay(v)}>
                {v === 0 ? 'Любая частота' : `от ${n(v)} сигн./день`}
              </Chip>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-textDim">
            <input type="checkbox" checked={robustOnly} onChange={(e) => setRobustOnly(e.target.checked)} />
            только <CheckCircle2 size={12} className="text-pulse" /> держится на обоих
          </label>
        </div>
        <details className="text-xs" open={extraOn || undefined}>
          <summary className="cursor-pointer select-none text-textDim hover:text-text">
            Ещё фильтры и сортировка{extraOn ? ' · включены' : ''}
          </summary>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            <Field label="Результат за сделку">
              <select className={INPUT} value={minMean ?? ''}
                onChange={(e) => setMinMean(e.target.value === '' ? null : Number(e.target.value))}>
                <option value="">любой</option>
                <option value="0">в плюсе в обоих периодах</option>
                <option value="0.3">от +0,3% в обоих</option>
                <option value="0.5">от +0,5% в обоих</option>
                <option value="1">от +1% в обоих</option>
              </select>
            </Field>
            <Field label="Надёжность">
              <select className={INPUT} value={minT ?? ''}
                onChange={(e) => setMinT(e.target.value === '' ? null : Number(e.target.value))}>
                <option value="">любая</option>
                <option value="1">от 1 в обоих</option>
                <option value="2">от 2 в обоих (вряд ли случайность)</option>
              </select>
            </Field>
            <Field label="Сделок в каждом периоде">
              <select className={INPUT} value={minTrades} onChange={(e) => setMinTrades(Number(e.target.value))}>
                <option value={0}>сколько угодно</option>
                <option value={30}>от 30</option>
                <option value={100}>от 100</option>
                <option value={300}>от 300</option>
              </select>
            </Field>
            <Field label="Сортировать">
              <select className={INPUT} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                {(Object.keys(SORTS) as SortKey[]).map((k) => <option key={k} value={k}>{SORTS[k].label}</option>)}
              </select>
            </Field>
          </div>
          {sort === 'check' && (
            <p className="text-2xs text-amber mt-1.5">
              Сортировка по проверке превращает проверку в подбор: лучшие строки сверху будут частично удачей.
            </p>
          )}
        </details>
      </div>
      {run && !isLoading && (
        <p className="text-xs text-textFaint mb-2">
          Подходит вариантов: {filtered.length}{filtered.length > 100 ? ' (показаны первые 100)' : ''}
        </p>
      )}

      {isLoading && <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">…</div>}
      {isError && (
        <div className="px-5 py-10 text-center text-rose text-sm bg-ink border border-line">Не удалось загрузить.</div>
      )}
      {!isLoading && !isError && !run && (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
          Пересчёт ещё не выполнялся.
        </div>
      )}
      {run && !isLoading && rows.length === 0 && (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
          Под эти фильтры вариантов нет.
        </div>
      )}
      {rows.length > 0 && <SetupTable rows={rows} baseline={baseline} universalId={universalId} />}
    </>
  )
}

function statusText(r: SetupRequest): ReactNode {
  if (r.status === 'queued') return <span className="text-textDim">в очереди{r.queue_pos ? ` (перед вами ${r.queue_pos})` : ''}</span>
  if (r.status === 'running') return <span className="text-sky inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> считается, ~1–2 мин</span>
  if (r.status === 'error') return <span className="text-rose">ошибка: {r.error}</span>
  return <span className="text-pulse">готово · {r.result?.n_events ?? 0} сигналов на истории</span>
}

function CustomTab({ universalId }: { universalId?: number }) {
  const [forms, setForms] = useState<MetricForms>(PRESETS[0].forms)
  const [err, setErr] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)
  const { data: requests = [] } = useSetupRequests()
  const create = useCreateSetupRequest()
  const resultsRef = useRef<HTMLDivElement>(null)

  async function submit() {
    setErr(null)
    const p = metricsToParams(forms)
    if (typeof p === 'string') { setErr(p); return }
    try {
      const r = await create.mutateAsync(p)
      setOpenId(r.id)
      // на телефоне результаты под длинной формой — показать, что расчёт пошёл
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErr(detail ?? 'Не удалось отправить на расчёт')
    }
  }

  const open = requests.find((r) => r.id === openId) ??
    requests.find((r) => r.status === 'done') ?? requests[0]

  return (
    <div className="grid grid-cols-1 2xl:grid-cols-[460px_1fr] gap-4 items-start">
      <div className="bg-ink border border-line p-4 space-y-3 max-w-2xl">
        <p className="text-xs text-textDim leading-snug">
          Задайте любые условия так же, как в скринере (цена и OI — до 100%, окно до 4 ч), — посчитаем
          их на всей истории: лонг и шорт, удержание 15 мин / 1 ч / 4 ч, подбор и проверка. Занимает
          1–2 минуты, можно уйти со страницы.
        </p>
        <MetricBlocks value={forms} onChange={setForms} />
        {err && <div className="text-rose text-sm">{err}</div>}
        <button
          onClick={submit}
          disabled={create.isPending}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-fire/15 text-fire border border-fire/40 hover:bg-fire/25 disabled:opacity-50"
        >
          <Calculator size={14} /> Посчитать на истории
        </button>
      </div>

      <div ref={resultsRef} className="space-y-3 min-w-0 scroll-mt-16">
        {requests.length === 0 && (
          <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
            Здесь появятся ваши расчёты.
          </div>
        )}
        {requests.length > 0 && (
          <div className="bg-ink border border-line divide-y divide-line/40">
            {requests.map((r) => (
              <button
                key={r.id} type="button" onClick={() => setOpenId(r.id)}
                className={cn('w-full text-left px-3 py-2 text-xs flex items-center gap-3 flex-wrap hover:bg-line/20',
                  open?.id === r.id && 'bg-line/30')}
              >
                <span className="text-text">{autoName(r.params)}</span>
                <span className="ml-auto">{statusText(r)}</span>
              </button>
            ))}
          </div>
        )}
        {open?.status === 'done' && open.result && (
          open.result.rows.length > 0 ? (
            <>
              <div className="bg-ink border border-line px-3 py-2 text-sm">
                <span className="text-textFaint text-xs">Условия: </span>{describe(open.params)}
                <span className="text-textFaint text-xs"> · {open.result.n_events} сигналов за {fmtDate(open.result.data_from)}–{fmtDate(open.result.data_to)}</span>
              </div>
              <BaselineLine b={open.result.baseline} />
              <SetupTable rows={open.result.rows} baseline={open.result.baseline}
                universalId={universalId} compact />
            </>
          ) : (
            <div className="px-5 py-8 text-center text-textFaint text-sm bg-ink border border-line">
              На истории эти условия ни разу не сработали — попробуйте пороги помягче.
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default function ScreenerSetups() {
  const [tab, setTab] = useState<'ready' | 'custom'>('ready')
  const { data: overview } = useScreenersOverview()
  const universalId = overview?.available.find((a) => a.def.kind === 'universal')?.def.id

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Link to="/app/screeners" className="inline-flex items-center gap-1 text-xs text-textFaint hover:text-textDim mb-2">
            <ArrowLeft size={13} /> Скринеры
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Подбор сетапов</h1>
        </div>
        <div className="flex border border-line">
          {([['ready', 'Готовые варианты'], ['custom', 'Свой вариант']] as const).map(([k, label]) => (
            <button
              key={k} onClick={() => setTab(k)}
              className={cn('px-3 py-1.5 text-sm', tab === k ? 'bg-line/40 text-text' : 'text-textDim hover:text-text')}
            >
              {label}
            </button>
          ))}
        </div>
      </header>
      <Help />
      {tab === 'ready' ? <ReadyTab universalId={universalId} /> : <CustomTab universalId={universalId} />}
    </div>
  )
}
